import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { RootStores } from '../modules/common/stores';
import { STORE_PROJECTS, STORE_ROUTER } from '../modules/common/constants';
import { AuthConsumer } from '../modules/authorization/AuthContext';
import SideMenuHeaderControls from './SideMenuHeaderControls';
import { Divider, Menu, Tooltip } from 'antd';
import { ProjectPagesNavigation } from '../modules/project_management/routes';
import { hasPermission } from '../modules/authorization/components/HasPermission';
import { AppPermissions } from '../modules/authorization/Permissions';
import { ApiOutlined, BarChartOutlined, DatabaseOutlined, ConsoleSqlOutlined, LockOutlined } from '@ant-design/icons';
import { AlphaVersionLabel } from '../modules/common/components';
import featureFlags from '../config/features';

type Props = RootStores;

type State = {
    collapsed: boolean;
    selectedItemKey: string;
    selectedKeys: string[];
};

@inject(STORE_ROUTER, STORE_PROJECTS)
@observer
export default class AdministrationSideMenu extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            collapsed: this.props.router!.sideCollapsed,
            selectedItemKey: '',
            selectedKeys: ['']
        };
    }

    componentDidMount = () => {
        const location = window.location.href;
        if (location.includes('indexing')) {
            this.setState({ selectedKeys: ['indexing'] });
        } else if (location.includes('alpha-configs')) {
            this.setState({ selectedKeys: ['alphaConfig'] });
        } else if (location.includes('home-shortcuts')) {
            this.setState({ selectedKeys: ['homeShortcuts'] });
        } else if (location.includes('codeSnippets')) {
            this.setState({ selectedKeys: ['codeSnippets'] });
        } else if (location.includes('script-management')) {
            this.setState({ selectedKeys: ['scriptManagement'] });
        } else if (location.includes('mongo-console')) {
            this.setState({ selectedKeys: ['mongoConsole'] });
        } else if (location.includes('web-request-console')) {
            this.setState({ selectedKeys: ['webRequestConsole'] });
        } else if (location.includes('ml-models')) {
            this.setState({ selectedKeys: ['mlModels'] });
        } else if (location.includes('api-keys')) {
            this.setState({ selectedKeys: ['apiKeys'] });
        } else {
            this.setState({ selectedKeys: ['technicalInformation'] });
        }
    };

    render() {
        const routerStore = this.props.router!;
        const projectsStore = this.props.projects!;

        const onSelectMenuItem = (key: string, type: string) => {
            this.setState(s => ({ ...s, selectedKeys: [key], selectedItemKey: key }));
            if (type != null) {
                switch (type) {
                    case 'indexing':
                        routerStore.pushToHistory(ProjectPagesNavigation.IndexingPage);
                        return;
                    case 'codeSnippets':
                        routerStore.pushToHistory(ProjectPagesNavigation.CodeSnippetsPage);
                        return;
                    case 'scriptManagement':
                        routerStore.pushToHistory(ProjectPagesNavigation.ScriptManagementPage);
                        return;
                    case 'mongoConsole':
                        routerStore.pushToHistory(ProjectPagesNavigation.MongoConsolePage);
                        return;
                    case 'webRequestConsole':
                        routerStore.pushToHistory(ProjectPagesNavigation.WebRequestConsolePage);
                        return;
                    case 'alphaConfig':
                        routerStore.pushToHistory(ProjectPagesNavigation.AlphaConfigPage);
                        return;
                    case 'homeShortcuts':
                        routerStore.pushToHistory(ProjectPagesNavigation.HomeShortcutsPage);
                        return;
                    case 'technicalInformation':
                        routerStore.pushToHistory(ProjectPagesNavigation.TechnicalInformationPage);
                        return;
                    case 'mlModels':
                        routerStore.pushToHistory(ProjectPagesNavigation.MlModelsManagementPage);
                        return;
                    case 'apiKeys':
                        routerStore.pushToHistory(ProjectPagesNavigation.ApiKeysPage);
                        return;
                    default:
                        return;
                }
            }
        };

        // eslint-disable-next-line max-len
        const menuItem = (
            type: string,
            icon: React.ReactElement,
            title: string,
            enabled: boolean,
            style: object | undefined = undefined,
            appRoles?: string[],
            permissionClaim?: AppPermissions
        ) => {
            if (!appRoles || hasPermission(appRoles, permissionClaim!)) {
                return (
                    <Menu.Item
                        className={this.state.selectedItemKey === type ? 'selected' : ''}
                        key={type}
                        disabled={!enabled}
                        onClick={() => onSelectMenuItem(type, type)}
                    >
                        <span style={style}>
                            <Tooltip title={title} placement="right">
                                {icon}
                            </Tooltip>
                            <span>{title}</span>
                        </span>
                    </Menu.Item>
                );
            } else {
                return <></>;
            }
        };

        return (
            <>
                <AuthConsumer>
                    {({ permissions }) => (
                        <>
                            <SideMenuHeaderControls routerStore={routerStore} projectsStore={projectsStore} />
                            <Divider style={{ marginTop: 0 }} />
                            <Menu
                                mode="inline"
                                defaultOpenKeys={['sub1']}
                                selectedKeys={this.state.selectedKeys}
                                style={{ overflow: 'hidden auto', borderRight: 0, maxHeight: 'calc(100vh - 195px)' }}
                                inlineIndent={18}
                            >
                                {menuItem('indexing', <i className="alpha-icon md list-icon" />, 'Indexing', true)}
                                {menuItem(
                                    'codeSnippets',
                                    <i className="alpha-icon md code-icon" />,
                                    'Code snippets',
                                    true,
                                    undefined,
                                    permissions,
                                    AppPermissions.CanManageCodeSnippets
                                )}
                                {menuItem(
                                    'scriptManagement',
                                    <i className="alpha-icon md code-icon" />,
                                    'Script management',
                                    true
                                )}
                                {menuItem(
                                    'mongoConsole',
                                    <ConsoleSqlOutlined style={{ marginLeft: 3 }} />,
                                    'Mongo Console',
                                    true
                                )}
                                {featureFlags.webRequestConsoleEnabled
                                    ? menuItem(
                                          'webRequestConsole',
                                          <ApiOutlined style={{ marginLeft: 3 }} />,
                                          'Web Request Console',
                                          true
                                      )
                                    : null}
                                {menuItem(
                                    'alphaConfig',
                                    <i className="alpha-icon md settings" />,
                                    'Alpha configs',
                                    true
                                )}
                                {menuItem(
                                    'homeShortcuts',
                                    <i className="alpha-icon md list-icon" />,
                                    'Home Screen Shortcuts',
                                    true,
                                    undefined,
                                    permissions,
                                    AppPermissions.CanManageShortcuts
                                )}
                                {menuItem(
                                    'technicalInformation',
                                    <BarChartOutlined style={{ marginLeft: 3 }} />,
                                    'Technical information',
                                    true
                                )}
                                {menuItem(
                                    'mlModels',
                                    <DatabaseOutlined style={{ marginLeft: 3 }} />,
                                    'ML Models Management',
                                    true
                                )}
                                {menuItem(
                                    'apiKeys',
                                    <LockOutlined style={{ marginLeft: 3 }} />,
                                    'API Keys',
                                    true,
                                    undefined,
                                    permissions,
                                    AppPermissions.CanAccessAdministration
                                )}
                            </Menu>
                            <div className="projects-side-menu-footer">
                                <AlphaVersionLabel />
                            </div>
                        </>
                    )}
                </AuthConsumer>
            </>
        );
    }
}
